import { useCallback, useEffect, useState } from 'react'

/**
 * Selection lives in the URL, so a refresh lands you back where you were and a view is
 * linkable.
 *
 * The URL holds the selection's IDENTITY (session name / project id), not the object —
 * on a cold load the agent and project lists haven't arrived yet, so App resolves the
 * identity against them once they do. Storing the object would mean guessing fields we
 * don't have.
 *
 * Params are merged, never replaced: the backend hands out `?t=<token>` on first visit
 * and unrelated params must survive our writes.
 */

export type Selection =
  | {
      kind: 'agent'
      // The stable identity is (hash, device): the channel hash the messaging layer
      // already uses, plus the device that disambiguates a name shared across
      // machines (a LOCAL agent hashes host="", so `main` collides cross-device
      // without it). session/host are the resolved / legacy handle — present for
      // in-app opens and old `?agent=<name>&host=` links, null on a cold load from a
      // hash URL until the agent list resolves it.
      session: string | null
      host: string | null
      hash?: string | null
      device?: string | null
    }
  | { kind: 'project'; id: string }
  // A friend DM: `peer` is the resolved email (in-app opens, legacy links); `key`
  // is the opaque dmKey the URL carries so a shared link never leaks the email.
  // `peer` may also be the dm's own conversation id (isDmConv): a group-mate who is
  // not a friend has no address to open it by (owner, 2026-10-18: 只有加好友之後 Email
  // 才會互相顯示).
  // `account` is WHICH friend when the address is two of them (the friend list's bff account_id):
  // owner, 2026-10-31 — friends with both accounts at one address, he could open neither.
  | { kind: 'dm'; peer: string | null; key?: string | null; account?: string | null }
  // A friend's agent, shared with this account (owner, 2026-10-17: it opens like an agent of your
  // own — 朋友 Agent 的 session ui 可以用右開的方式). The SHARE names it, never a machine or a session.
  | { kind: 'shared'; id: string }
  | null

export type UrlState = {
  sel: Selection
  /** Display mode for an agent. Absent ⇒ fall back to the stored preference. */
  view: string | null
}

/**
 * A stable, opaque handle for a friend DM in the URL, derived from the peer's
 * email — so a shared link carries no plaintext email. NOT a secret (the email
 * space is enumerable from candidates); it only keeps PII out of the address bar,
 * and is reversed against the friends list. Two independent 32-bit FNV-style
 * passes folded to 48 bits, so two friends never collide in practice.
 */
export function dmKey(email: string): string {
  const s = email.trim().toLowerCase()
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h = ((h ^ s.charCodeAt(i)) * 0x01000193) >>> 0
  }
  let g = (0x9e3779b1 ^ s.length) >>> 0
  for (let i = s.length - 1; i >= 0; i--) {
    g = ((g ^ s.charCodeAt(i)) * 0x85ebca77) >>> 0
  }
  return (h.toString(16).padStart(8, '0') + g.toString(16).padStart(8, '0')).slice(0, 12)
}

/** A dm named by its conversation id (16 hex) rather than by a friend's address or dmKey (12 hex). */
export function isDmConv(s: string | null | undefined): s is string {
  return !!s && /^[0-9a-f]{16}$/.test(s)
}

export function parse(search: string): UrlState {
  const q = new URLSearchParams(search)
  const view = q.get('view')
  const project = q.get('project')
  if (project) return { sel: { kind: 'project', id: project }, view }
  const shared = q.get('shared')
  if (shared && /^[0-9a-f]{12,64}$/.test(shared)) return { sel: { kind: 'shared', id: shared }, view }
  const dm = q.get('dm')
  if (dm) {
    const acct = q.get('acct')
    const account = acct && /^[0-9a-f]{16}$/.test(acct) ? { account: acct } : {}
    // An email (has '@') is a legacy ?dm=<email> link; a 12-hex token is the
    // opaque dmKey that keeps a friend's email out of a shared URL.
    if (!dm.includes('@') && /^[0-9a-f]{12}$/.test(dm)) {
      return { sel: { kind: 'dm', peer: null, key: dm, ...account }, view }
    }
    // 16 hex: the dm's conversation id — a group-mate's dm, which no address opens.
    if (isDmConv(dm)) return { sel: { kind: 'dm', peer: dm, key: null }, view }
    return { sel: { kind: 'dm', peer: dm, key: null, ...account }, view }
  }
  const agent = q.get('agent')
  if (agent) {
    // A 12-hex `agent` (optionally with `device`) is the stable channel-hash form.
    // Anything else is a legacy `?agent=<name>&host=<host>` link, still honoured so
    // old bookmarks and shared URLs keep resolving.
    if (/^[0-9a-f]{12}$/.test(agent)) {
      return {
        sel: { kind: 'agent', session: null, host: null, hash: agent, device: q.get('device') || null },
        view,
      }
    }
    // `device` is kept here too: a bare terminal (the rail's Terminals rows) has no channel
    // hash, so its URL is always this form, and its name is only unique per machine.
    return {
      sel: { kind: 'agent', session: agent, host: q.get('host') || null, hash: null, device: q.get('device') || null },
      view,
    }
  }
  return { sel: null, view }
}

export function serialize(state: UrlState): string {
  // Start from what's already there so `t` and anything else is preserved.
  const q = new URLSearchParams(window.location.search)
  q.delete('agent')
  q.delete('host')
  q.delete('device')
  q.delete('project')
  q.delete('dm')
  q.delete('shared')
  q.delete('view')

  if (state.sel?.kind === 'agent') {
    // Prefer the stable channel hash + device; fall back to name+host for a
    // selection not yet enriched (a just-created session) or a terminal, which has
    // no channel hash.
    if (state.sel.hash) {
      q.set('agent', state.sel.hash)
      if (state.sel.device) q.set('device', state.sel.device)
    } else if (state.sel.session) {
      q.set('agent', state.sel.session)
      if (state.sel.host) q.set('host', state.sel.host)
    }
  } else if (state.sel?.kind === 'project') {
    q.set('project', state.sel.id)
  } else if (state.sel?.kind === 'dm') {
    // Write the opaque key; hash the email on the way out when the selection only
    // carries the email (an in-app open), so a shared link never leaks it.
    // A dm opened by its conversation id stays that id — it names nobody already.
    const dm =
      state.sel.key ?? (isDmConv(state.sel.peer) ? state.sel.peer : state.sel.peer ? dmKey(state.sel.peer) : '')
    if (dm) q.set('dm', dm)
    // Which of two friends at that address — a handle, not the address, so it leaks nothing.
    if (dm && state.sel.account && !isDmConv(dm)) q.set('acct', state.sel.account)
  } else if (state.sel?.kind === 'shared') {
    q.set('shared', state.sel.id)
  }
  if (state.view) q.set('view', state.view)

  const s = q.toString()
  return s ? `?${s}` : window.location.pathname
}

export function useUrlState() {
  const [state, setState] = useState<UrlState>(() => parse(window.location.search))

  // Back/forward should move between conversations rather than leaving the app.
  useEffect(() => {
    const onPop = () => setState(parse(window.location.search))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  /**
   * `push` adds a history entry (a deliberate navigation, so Back works); `replace`
   * rewrites the current one (for incidental changes like flipping the display mode,
   * which shouldn't fill the history stack).
   */
  const write = useCallback((next: UrlState, mode: 'push' | 'replace') => {
    const url = serialize(next)
    if (mode === 'push') window.history.pushState(null, '', url)
    else window.history.replaceState(null, '', url)
    setState(next)
  }, [])

  return { state, write }
}
